import { useState, useRef } from "react";
import { Upload, FileImage, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface CompactFileSelectorProps {
  onFilesSelected: (files: File[]) => void;
  selectedFiles: File[];
}

export const CompactFileSelector = ({ onFilesSelected, selectedFiles }: CompactFileSelectorProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      onFilesSelected([...selectedFiles, ...files]);
      toast({
        title: "Arquivos adicionados",
        description: `${files.length} arquivo(s) adicionado(s) à seleção.`,
      });
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const clearAllFiles = () => {
    onFilesSelected([]);
    toast({
      title: "Seleção limpa",
      description: "Todos os arquivos foram removidos da seleção.",
    });
  };

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    onFilesSelected(newFiles);
    toast({
      title: "Arquivo removido",
      description: `${selectedFiles[index].name} foi removido da seleção.`,
    });
  };

  return (
    <div className="flex items-center space-x-4">
      {/* Botão de Seleção */}
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          onClick={handleButtonClick}
          variant="outline"
          className="h-12 px-6 border-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/60 hover:bg-muted/20 transition-all duration-200 bg-transparent"
        >
          <Upload className="h-5 w-5 mr-2" />
          Selecionar Fotos
        </Button>
      </div>

      {/* Rótulo com Total */}
      {selectedFiles.length > 0 && (
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 px-3 py-2 bg-muted/50 rounded-lg border">
            <FileImage className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {selectedFiles.length} {selectedFiles.length === 1 ? 'arquivo' : 'arquivos'} selecionado{selectedFiles.length > 1 ? 's' : ''}
            </span>
            <Button
              onClick={clearAllFiles}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          {/* Lista Compacta de Arquivos (máximo 3) */}
          <div className="flex items-center space-x-2">
            {selectedFiles.slice(0, 3).map((file, index) => (
              <div
                key={index}
                className="flex items-center space-x-1 px-2 py-1 bg-primary/10 rounded-md text-xs"
              >
                <span className="max-w-20 truncate">{file.name}</span>
                <Button
                  onClick={() => removeFile(index)}
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                >
                  <X className="h-2 w-2" />
                </Button>
              </div>
            ))}
            {selectedFiles.length > 3 && (
              <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded-md">
                +{selectedFiles.length - 3} mais
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};